import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Modal } from '@/components/ui/Modal';
import { Stack } from '@/components/ui/Stack';
import { Card } from '@/components/ui/Card';
import { Autocomplete } from '@/components/ui/Autocomplete';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ArrowRightLeft, Calendar, MapPin } from 'lucide-react';
import { useItineraryFormOptions } from '@/hooks/useItineraryFormOptions';
import { CargoDetailsService } from '@/services/cargoDetailsService';
import { ItineraryItem, UpdateItineraryRequest } from '@/types/cargoList.types';
import { getWriteErrorMessage } from '@/utils/apiError';
import { toDateTimeInputValues } from '@/utils/dateUtils';

interface UpdateItineraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  cargoId: number;
  typeId: number;
  fixtureId: number;
  item: ItineraryItem;
  onSuccess?: () => void;
}

interface UpdateItineraryFormValues {
  purpose: string;
  port: string;
  via: string;
  arrivalDate: string;
  arrivalTime: string;
  departureDate: string;
  departureTime: string;
  speed: number | '';
}

const validationSchema = Yup.object({
  purpose: Yup.string().required('Purpose is required'),
  port: Yup.string().required('Port is required'),
  via: Yup.string().required('Via is required'),
  arrivalDate: Yup.string().required('Arrival date is required'),
  arrivalTime: Yup.string().required('Arrival time is required'),
  departureDate: Yup.string().required('Departure date is required'),
  departureTime: Yup.string().required('Departure time is required'),
  speed: Yup.number()
    .required('Speed is required')
    .min(1, 'Speed must be greater than 0')
    .max(50, 'Speed cannot exceed 50 knots'),
});

export const UpdateItineraryModal: React.FC<UpdateItineraryModalProps> = ({
  isOpen,
  onClose,
  typeId,
  fixtureId,
  item,
  onSuccess,
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    portOptions,
    purposeOptions,
    viaPointOptions,
    loading,
    error,
    searchPorts,
    searchViaPoints,
  } = useItineraryFormOptions();

  // Convert item dates back to form format
  const getInitialValues = (): UpdateItineraryFormValues => {
    const arrival = toDateTimeInputValues(item.arrivalTime);
    const departure = toDateTimeInputValues(item.departureTime);

    return {
      purpose: item.purpose || '',
      port: item.port || '',
      via: item.via || '',
      arrivalDate: arrival.date,
      arrivalTime: arrival.time,
      departureDate: departure.date,
      departureTime: departure.time,
      speed: item.speed,
    };
  };

  const handleSubmit = async (
    values: UpdateItineraryFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      // Resolve the option by the current form value (the Autocomplete sets
      // option.id on selection). Fall back to the original id/name only when
      // the form value is still the original name — otherwise an option that
      // exists at item.portId would always win over a freshly-selected one.
      const resolveOption = <T extends { id: number | string; name: string }>(
        options: T[],
        value: unknown,
        originalName: string | null | undefined,
        originalId: number | null | undefined
      ): T | { id: number; name: string } | undefined => {
        const found = options.find((o) => String(o.id) === String(value) || o.name === value);
        if (found) return found;
        if (value && value === originalName && originalId != null) {
          return { id: originalId, name: originalName };
        }
        return undefined;
      };

      const selectedPort = resolveOption(portOptions, values.port, item.port, item.portId);
      const selectedPurpose = resolveOption(
        purposeOptions,
        values.purpose,
        item.purpose,
        item.purposeId
      );
      const selectedVia = resolveOption(viaPointOptions, values.via, item.via, item.viaId);

      if (!selectedPort) throw new Error('Selected port not found');
      if (!selectedPurpose) throw new Error('Selected purpose not found');
      if (!selectedVia) throw new Error('Selected via point not found');

      // Parse date+time as local (no trailing 'Z') so the value the user typed
      // matches what they see rendered. toISOString then converts to UTC for the API.
      const arrivalTime = new Date(`${values.arrivalDate}T${values.arrivalTime}:00`);
      const departureTime = new Date(`${values.departureDate}T${values.departureTime}:00`);

      const requestData: UpdateItineraryRequest = {
        PurposeId: Number(selectedPurpose.id),
        PortId: Number(selectedPort.id),
        ViaId: Number(selectedVia.id),
        ArrivalTime: arrivalTime.toISOString(),
        DepartureTime: departureTime.toISOString(),
        Speed: Number(values.speed),
        Order: item.order, // Keep the original order
      };

      await CargoDetailsService.updateItineraryItem(
        typeId,
        fixtureId,
        item.order, // previous order for URL path
        requestData
      );

      // The API answers 204, so there is no entity to merge into the store -
      // onSuccess refetches the itinerary from the server instead.
      onSuccess?.();
      onClose();
    } catch (error) {
      setSubmitError(getWriteErrorMessage(error, 'Failed to update itinerary item'));
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <Modal title="Edit Itinerary Item" isOpen={isOpen} onClose={onClose}>
        <div className="py-8 text-center">
          <p className="text-red-600 dark:text-red-400">Error loading reference data: {error}</p>
          <Button onClick={onClose} className="mt-4">
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Edit Itinerary Item" isOpen={isOpen} onClose={onClose}>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form className="space-y-6">
            {submitError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
              </div>
            )}

            {/* Location & Purpose Section */}
            <Card title="Location & Purpose" className="bg-gray-50 dark:bg-dark-800">
              <Stack direction="vertical" spacing={4}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <MapPin className="mr-1 inline h-4 w-4" />
                      Port
                    </label>
                    <Autocomplete
                      name="port"
                      options={portOptions}
                      onSearch={searchPorts}
                      loading={loading}
                      allowCustomValue={false}
                      placeholder="Select or search for a port..."
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Purpose
                    </label>
                    <Autocomplete
                      name="purpose"
                      options={purposeOptions}
                      loading={loading}
                      allowCustomValue={false}
                      placeholder="Select purpose..."
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <ArrowRightLeft className="mr-1 inline h-4 w-4" />
                    Via
                  </label>
                  <Autocomplete
                    name="via"
                    options={viaPointOptions}
                    onSearch={searchViaPoints}
                    loading={loading}
                    allowCustomValue={false}
                    placeholder="Select or search for via point..."
                  />
                </div>
              </Stack>
            </Card>

            {/* Navigation Details Section */}
            <Card title="Navigation Details" className="bg-gray-50 dark:bg-dark-800">
              <Stack direction="vertical" spacing={4}>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Speed (knots)
                  </label>
                  <Input
                    name="speed"
                    type="number"
                    min="1"
                    max="50"
                    step="0.1"
                    placeholder="Enter speed..."
                  />
                </div>
              </Stack>
            </Card>

            {/* Schedule Section */}
            <Card title="Schedule" className="bg-gray-50 dark:bg-dark-800">
              <Stack direction="vertical" spacing={4}>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Calendar className="mr-1 inline h-4 w-4" />
                    Arrival
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input name="arrivalDate" type="date" placeholder="Select date..." />
                    <Input name="arrivalTime" type="time" placeholder="Select time..." />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Calendar className="mr-1 inline h-4 w-4" />
                    Departure
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input name="departureDate" type="date" placeholder="Select date..." />
                    <Input name="departureTime" type="time" placeholder="Select time..." />
                  </div>
                </div>
              </Stack>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={loading}>
                Save
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};
